import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  AddCircle as AddIcon,
  CreditCard as CreditCardIcon,
  Assessment as ReportsIcon,
} from '@mui/icons-material';
import { triggerHaptic } from '../utils/mobileUX';
import { getSafeAreaPadding } from '../utils/mobileUX';

/**
 * Mobile Bottom Navigation
 * Provides thumb-friendly navigation at bottom of screen
 * Includes iOS safe-area support for home indicator
 */
const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Map routes to bottom nav values
  const getNavValue = (pathname) => {
    if (pathname.startsWith('/dashboard')) return 0;
    if (pathname.startsWith('/expenses')) return 1;
    if (pathname.startsWith('/add-expense')) return 2;
    if (pathname.startsWith('/credit-cards')) return 3;
    if (pathname.startsWith('/reports') || pathname.startsWith('/analytics')) return 4;
    return 0; // Default to dashboard
  };

  const navValue = getNavValue(location.pathname);

  const handleNavChange = (event, newValue) => {
    triggerHaptic('selection'); // Haptic feedback on navigation
    const routes = ['/dashboard', '/expenses', '/add-expense', '/credit-cards', '/analytics'];
    navigate(routes[newValue]);
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        display: { xs: 'block', md: 'none' }, // Only show on mobile
        borderTop: 1,
        borderColor: 'divider',
        ...getSafeAreaPadding({ bottom: true }), // iOS safe area
      }}
      elevation={8}
    >
      <BottomNavigation
        value={navValue}
        onChange={handleNavChange}
        showLabels
        sx={{
          height: 'auto',
          minHeight: 56,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 64,
            maxWidth: 168,
            padding: '6px 12px 8px',
            '&.Mui-selected': {
              paddingTop: '6px',
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.75rem',
            '&.Mui-selected': {
              fontSize: '0.75rem',
            },
          },
        }}
      >
        <BottomNavigationAction
          label="Dashboard"
          icon={<DashboardIcon />}
          aria-label="Navigate to Dashboard"
        />
        <BottomNavigationAction
          label="Expenses"
          icon={<ReceiptIcon />}
          aria-label="Navigate to Expenses"
        />
        <BottomNavigationAction
          label="Add"
          icon={<AddIcon />}
          aria-label="Add new expense"
        />
        <BottomNavigationAction
          label="Cards"
          icon={<CreditCardIcon />}
          aria-label="Navigate to Credit Cards"
        />
        <BottomNavigationAction
          label="Reports"
          icon={<ReportsIcon />}
          aria-label="Navigate to Reports"
        />
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNav;
